/* eslint-disable react/prop-types */
import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'interview/i18n'
import {
  fetchPersonalInterview,
  createCalendarEvent,
} from 'interview/actions/personal-interview'
import FetchWithSpinner from 'components/fetch-with-spinner'
import Layout from 'interview/components/layout'
import { parseAvailablePeriods } from 'utils/scheduling'
import { get, isEmpty, delay } from 'lodash'
import moment from 'moment-timezone'
import { SlotPicker } from 'cronofy-elements'
import { DATETIME_TIMEZONE_FORMAT } from 'app-constants'
import { Flex } from 'components/styled/containers/flex'
import { ExternalLinkButton } from 'components/styled/button'
import { Box } from 'components/styled/containers/box'
import { Body, H3 } from 'components/styled/typography'
import { LoadingIndicator } from 'components/styled/loading-indicator'
import {
  PrimaryStyle,
  SecondaryStyle,
} from '../../components/styled/button/base-button'

const propTypes = {
  dispatch: PropTypes.func.isRequired,
  i18n: PropTypes.shape({}).isRequired,
  params: PropTypes.shape({}).isRequired,
  personalInterview: PropTypes.shape({}),
}

const defaultProps = {
  personalInterview: undefined,
}

const mapStateToProps = state => ({
  personalInterview: state.interview.personalInterview,
})

const StyledSlotPicker = styled.div`
  .slotpicker__confirmation-button {
    ${PrimaryStyle};
  }

  .slotpicker__confirmation-button--back {
    ${SecondaryStyle};
  }
`

const PersonalInterview = ({ i18n, dispatch, personalInterview, params }) => {
  const renderSlotPicker = () => {
    if (
      !personalInterview ||
      !personalInterview.timeSlots ||
      !personalInterview.elementToken
    ) {
      return ''
    }

    const { timezone } = personalInterview.timeSlots

    const availablePeriods = parseAvailablePeriods({
      startDate: moment.tz(personalInterview.timeSlots.startDate, timezone),
      endDate: moment.tz(personalInterview.timeSlots.endDate, timezone),
      startTime: moment.tz(personalInterview.timeSlots.startTime, timezone),
      endTime: moment.tz(personalInterview.timeSlots.endTime, timezone),
    })

    if (isEmpty(availablePeriods)) {
      return ''
    }

    let members
    if (isEmpty(personalInterview.selectedTimeSlots)) {
      members = personalInterview.elementToken.subs.map(sub => ({ sub }))
    } else {
      members = personalInterview.selectedTimeSlots.map(timeslot => ({
        sub: timeslot.sub,
        available_periods: timeslot.availablePeriods.map(period => ({
          start: period.start,
          end: period.end,
        })),
      }))
    }

    return (
      <div>
        <div id="cronofy-slot-picker" />
        <div
          style={{
            display: 'none',
          }}
        >
          {personalInterview.elementToken &&
            !isEmpty(availablePeriods) &&
            // eslint-disable-next-line no-undef
            delay(
              () =>
                SlotPicker({
                  element_token: personalInterview.elementToken.token,
                  target_id: 'cronofy-slot-picker',
                  availability_query: {
                    participants: [
                      {
                        required: 'all',
                        members,
                      },
                    ],
                    required_duration: {
                      minutes: personalInterview.timeSlots.slotLength,
                    },
                    available_periods: availablePeriods,
                    max_results: 200,
                    start_interval: {
                      minutes: 15,
                    },
                  },
                  data_center: 'de',
                  styles: {
                    prefix: 'slotpicker',
                  },
                  callback: result => {
                    dispatch(
                      createCalendarEvent(result.notification.slot, params.id)
                    )
                  },
                }),
              100
            )}
        </div>
      </div>
    )
  }

  const loading = get(personalInterview, 'isLoading', false)
  const slotPicked = !isEmpty(
    get(personalInterview, ['pickedTimeSlot', 'participants'])
  )
  const position = get(personalInterview, ['position', 'item', 'title'], '')
  const location = get(personalInterview, ['event', 'location'], '')
  const startDate = get(personalInterview, ['timeSlots', 'startDate'], '')
  const eventStartTime = get(personalInterview, ['pickedTimeSlot', 'start'])
  const browserTimezone = moment.tz.guess()
  const personalInterviewTimezone = get(personalInterview, [
    'timeSlots',
    'timezone',
  ])

  return (
    <FetchWithSpinner action={() => fetchPersonalInterview(params)}>
      <Layout size="large">
        {loading && <LoadingIndicator />}
        {!loading && !slotPicked && (
          <>
            <Flex flexDirection="column" alignItems="center">
              <Box my="s20">
                <H3>{i18n.personalInterview.title}</H3>
              </Box>
              <Body mb="s0">{i18n.personalInterview.description[0]}</Body>
              <Body mb="s0" textAlign="center">
                {i18n.personalInterview.position} <strong>{position}</strong>{' '}
              </Body>
              <Body textAlign="center">
                {i18n.personalInterview.location} <strong>{location}</strong>
              </Body>
              <Body mb="s0">{i18n.personalInterview.description[1]}</Body>
              <Body>{i18n.personalInterview.description[2]}</Body>
              {browserTimezone !== personalInterviewTimezone && (
                <Box my="s24" textAlign="left">
                  <Body mb="s0">
                    <strong>{i18n.personalInterview.timezoneNotice[0]}</strong>
                  </Body>
                  <Body mb="s0">
                    {i18n.personalInterview.timezoneNotice[1]} {browserTimezone}{' '}
                    {moment.tz(startDate, browserTimezone).format('Z z')}
                  </Body>
                  <Body mb="s0">
                    {i18n.personalInterview.timezoneNotice[2]}{' '}
                    {personalInterviewTimezone}{' '}
                    {moment
                      .tz(startDate, personalInterviewTimezone)
                      .format('Z z')}
                  </Body>
                </Box>
              )}
            </Flex>
            <StyledSlotPicker>{renderSlotPicker()}</StyledSlotPicker>
          </>
        )}
        {!loading && slotPicked && (
          <Flex flexDirection="column" alignItems="center">
            <Box my="s20">
              <H3 my="s20">{i18n.personalInterview.success.title}</H3>
            </Box>
            <Body mb="s0">{i18n.personalInterview.success.description}</Body>
            <Body mb="s0" textAlign="center">
              {i18n.personalInterview.position} <strong>{position}</strong>
            </Body>
            <Body mb="s0" textAlign="center">
              {i18n.personalInterview.location} <strong>{location}</strong>
            </Body>
            <Body>
              {i18n.personalInterview.time}{' '}
              <strong>
                {moment(eventStartTime)
                  .tz(personalInterviewTimezone)
                  .format(DATETIME_TIMEZONE_FORMAT)}
              </strong>
            </Body>
            <Body textAlign="center">
              {i18n.personalInterview.success.linkDescription[0]}
            </Body>
            <ExternalLinkButton
              my="s20"
              target="_blank"
              href={`/api/v1/personal-interview/calendar-event/${params.id}`}
              text={i18n.personalInterview.success.link}
              bsStyle="primary"
            />
          </Flex>
        )}
      </Layout>
    </FetchWithSpinner>
  )
}

PersonalInterview.propTypes = propTypes
PersonalInterview.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  PersonalInterview
)
